import React, { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { apiConnectorPost } from "../../services/apiconnector";
import { endpoint } from "../../services/urls";
import toast from "react-hot-toast";

// ── shared tokens (FinoRaw palette) ──────────────────────────────────────────
const C = {
  pageBg:      "#000000",
  cardBg:      "#131920",
  inputBg:     "#0d1117",
  border:      "1px solid rgba(255,215,0,0.18)",
  borderFocus: "1px solid rgba(255,215,0,0.55)",
  gold:        "#f5a623",
  accent:      "#00e5ff",
  green:       "#00b578",
  greenBtn:    "linear-gradient(135deg, #00b578 0%, #00e676 100%)",
  red:         "#ff5252",
  textPrimary: "#ffffff",
  textMuted:   "#9ca3af",
  textDim:     "#6b7280",
};

function fmtDate(str) {
  if (!str) return "-";
  const d = new Date(str);
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

// ── History view ───────────────────────────────────────────────────────────────
function HistoryView() {
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ["p2p_transfer_history", page],
    queryFn: () =>
      apiConnectorPost(endpoint.get_report_details, {
        search:     "",
        created_at: "",
        updated_at: "",
        page,
        count:      "10",
        sub_label:  "FUND WALLET",
        main_label: "OUT",
      }),
    keepPreviousData: true,
  });

  const rows       = data?.data?.result?.data || [];
  const totalPages = Math.max(1, data?.data?.result?.total_pages || 1);

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: "40px 0" }}>
        <div style={{
          width: "34px", height: "34px", borderRadius: "50%",
          border: "3px solid rgba(245,166,35,0.15)",
          borderTopColor: C.gold,
          animation: "spin 0.8s linear infinite",
          margin: "0 auto",
        }} />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "40px 0" }}>
        <div style={{ fontSize: "34px", marginBottom: "12px" }}>⇄</div>
        <p style={{ color: C.textDim, fontSize: "14px" }}>No transfer history yet</p>
      </div>
    );
  }

  return (
    <div>
      {rows.map((row, i) => (
        <div
          key={row?.tr07_trans_id || i}
          style={{
            background: C.inputBg, borderRadius: "12px",
            border: C.border, padding: "14px", marginBottom: "10px",
          }}
        >
          {/* Row 1: TX id + amount */}
          <div style={{
            display: "flex", justifyContent: "space-between",
            alignItems: "center", marginBottom: "7px",
          }}>
            <span style={{ color: C.textDim, fontSize: "11px" }}>
              #{row?.tr07_trans_id || "-"}
            </span>
            <span style={{ color: C.gold, fontWeight: "700", fontSize: "15px" }}>
              ${parseFloat(row?.tr07_tr_amount || 0).toFixed(3)} USDT
            </span>
          </div>

          {/* Row 2: date */}
          <p style={{ color: C.textDim, fontSize: "11px", marginBottom: "8px" }}>
            {fmtDate(row?.tr07_created_at)}
          </p>

          {/* Row 3: from wallet + status */}
          <div style={{
            display: "flex", alignItems: "center",
            justifyContent: "space-between", marginBottom: "8px",
          }}>
            <span style={{
              background: "rgba(245,166,35,0.08)",
              border: "1px solid rgba(245,166,35,0.18)",
              borderRadius: "6px", padding: "3px 10px",
              color: C.gold, fontSize: "11px", fontWeight: "600",
            }}>
              {row?.tr07_sub_label || "Fund Wallet"}
            </span>
            <span style={{
              background: "rgba(0,181,120,0.1)",
              border: "1px solid rgba(0,181,120,0.25)",
              borderRadius: "6px", padding: "3px 10px",
              color: C.green, fontSize: "11px", fontWeight: "700",
            }}>
              ✓ Success
            </span>
          </div>

          {/* Row 4: description */}
          {row?.tr07_description && (
            <p style={{
              color: C.textMuted, fontSize: "11px",
              background: "rgba(255,255,255,0.02)",
              borderRadius: "6px", padding: "6px 8px",
              border: "1px solid rgba(255,255,255,0.04)",
            }}>
              {row.tr07_description}
            </p>
          )}
        </div>
      ))}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: "flex", justifyContent: "center",
          alignItems: "center", gap: "12px", marginTop: "16px",
        }}>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              background: "rgba(245,166,35,0.06)",
              border: "1px solid rgba(245,166,35,0.2)",
              borderRadius: "8px",
              color: page === 1 ? "#374151" : C.gold,
              fontSize: "13px", padding: "7px 16px",
              cursor: page === 1 ? "not-allowed" : "pointer",
            }}
          >← Prev</button>
          <span style={{ color: C.textDim, fontSize: "12px" }}>
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{
              background: "rgba(245,166,35,0.06)",
              border: "1px solid rgba(245,166,35,0.2)",
              borderRadius: "8px",
              color: page === totalPages ? "#374151" : C.gold,
              fontSize: "13px", padding: "7px 16px",
              cursor: page === totalPages ? "not-allowed" : "pointer",
            }}
          >Next →</button>
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function P2PTransfer() {
  const [view, setView]                     = useState("form"); // "form" | "history"
  const [transferAmount, setTransferAmount] = useState("");
  const [customerId, setCustomerId]         = useState("");
  const [walletType, setWalletType]         = useState("fund_wallet");
  const [recipientName, setRecipientName]   = useState("");
  const [recipientStatus, setRecipientStatus] = useState("idle");
  const [submitting, setSubmitting]         = useState(false);
  const [showConfirm, setShowConfirm]       = useState(false);
  const debounceRef                         = useRef(null);
  const queryClient                         = useQueryClient();

  // Profile / wallet balance
  const { data: profileData } = useQuery({
    queryKey: ["member_details_p2p"],
    queryFn: () =>
      import("../../services/apiconnector").then(({ apiConnectorGet }) =>
        apiConnectorGet(endpoint.member_details)
      ),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  const profile        = profileData?.data?.result?.[0] || {};
  const fundBalance    = parseFloat(profile?.tr03_fund_wallet  || 0);
  const topupBalance   = parseFloat(profile?.tr03_fund_wallet || 0);
  const currentBalance  = walletType === "fund_wallet" ? fundBalance : topupBalance;
  const isSelfTransfer  = walletType === "topup_wallet";

  // Auto-fill / clear Customer ID when wallet type switches
  useEffect(() => {
    if (isSelfTransfer) {
      const ownId = profile?.lgn_cust_id || "";
      setCustomerId(ownId);
      setRecipientName("Self Transfer");
      setRecipientStatus("found");
    } else {
      setCustomerId("");
      setRecipientName("");
      setRecipientStatus("idle");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletType]);

  // Debounced recipient name lookup (skipped for self-transfer)
  useEffect(() => {
    if (isSelfTransfer) return;
    if (!customerId.trim()) {
      setRecipientName("");
      setRecipientStatus("idle");
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setRecipientStatus("loading");
      try {
        const res = await axios.post(
          endpoint.get_spon_name,
          { customer_id: customerId },
          { headers: { "Content-Type": "application/json" } }
        );
        if (res?.data?.success) {
          setRecipientName(res.data.result?.[0]?.lgn_name || "Unknown");
          setRecipientStatus("found");
        } else {
          setRecipientName("Invalid Customer ID");
          setRecipientStatus("invalid");
        }
      } catch {
        setRecipientName("Error fetching user");
        setRecipientStatus("invalid");
      }
    }, 600);
    return () => clearTimeout(debounceRef.current);
  }, [customerId, isSelfTransfer]);

  const validate = () => {
    if (!transferAmount || Number(transferAmount) <= 0) {
      toast.error("Please enter a valid transfer amount");
      return false;
    }
    if (!customerId.trim()) {
      toast.error("Please enter a Customer ID");
      return false;
    }
    if (recipientStatus === "invalid") {
      toast.error("Invalid Customer ID");
      return false;
    }
    if (currentBalance < Number(transferAmount)) {
      toast.error("Insufficient balance in selected wallet");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    setShowConfirm(false);
    setSubmitting(true);
    const res = await apiConnectorPost(endpoint.member_fund_transfer, {
      pkg_amount:  transferAmount,
      pkg_id:      1,
      to_cust_id:  customerId,
      wallet_type: walletType,
    });
    setSubmitting(false);
    if (res?.data?.success) {
      toast.success(res.data.message || "Transfer successful!");
      setTransferAmount("");
      setCustomerId("");
      setRecipientName("");
      setRecipientStatus("idle");
      queryClient.invalidateQueries({ queryKey: ["member_details_p2p"] });
      queryClient.invalidateQueries({ queryKey: ["p2p_transfer_history"] });
    } else {
      toast.error(res?.data?.message || "Transfer failed. Please try again.");
    }
  };

  return (
    <div style={{
      minHeight: "100vh", background: C.pageBg, display: "flex",
      flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: "20px 16px 100px",
    }}>
      <div style={{
        width: "100%", maxWidth: "420px", background: C.cardBg,
        borderRadius: "20px", border: "1px solid rgba(255,215,0,0.14)",
        boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
        position: "relative", overflow: "hidden",
      }}>

        {/* Decorative left accent strip */}
        <div style={{
          position: "absolute", left: 0, top: 0, bottom: 0, width: "3px",
          background: `linear-gradient(to bottom, ${C.accent}, ${C.gold}, ${C.green})`,
        }} />

        {/* Decorative corner accents */}
        <div style={{
          position: "absolute", top: 0, right: 0, width: "72px", height: "72px",
          borderTop: "2px solid rgba(0,229,255,0.15)",
          borderRight: "2px solid rgba(0,229,255,0.15)",
          borderRadius: "0 20px 0 0",
        }} />
        <div style={{
          position: "absolute", bottom: 0, left: 0, width: "56px", height: "56px",
          borderBottom: "2px solid rgba(245,166,35,0.12)",
          borderLeft: "2px solid rgba(245,166,35,0.12)",
          borderRadius: "0 0 0 20px",
        }} />

        {/* ── Card content ── */}
        <div style={{ padding: "28px 24px 28px 28px", position: "relative", zIndex: 1 }}>

          {/* ── Header row: title + History toggle ── */}
          <div style={{
            display: "flex", alignItems: "flex-start",
            justifyContent: "space-between", marginBottom: "20px",
          }}>
            {/* Left: dot + title */}
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "5px" }}>
                <span style={{
                  width: "10px", height: "10px", borderRadius: "50%",
                  background: C.accent, boxShadow: `0 0 8px ${C.accent}`,
                  flexShrink: 0, animation: "pulse 2s infinite",
                }} />
                <span style={{ fontSize: "20px", fontWeight: "800", color: C.accent, letterSpacing: "-0.3px" }}>
                  P2P Transfer
                </span>
              </div>
              <p style={{ color: C.textDim, fontSize: "13px", marginLeft: "20px" }}>
                Transfer funds directly to another user
              </p>
            </div>

            {/* History / Back button */}
            <button
              onClick={() => setView(view === "form" ? "history" : "form")}
              style={{
                flexShrink: 0, marginLeft: "12px",
                background: view === "history"
                  ? "rgba(245,166,35,0.14)"
                  : "rgba(245,166,35,0.06)",
                border: "1px solid rgba(245,166,35,0.3)",
                borderRadius: "10px", color: C.gold,
                fontSize: "12px", fontWeight: "600",
                padding: "7px 13px", cursor: "pointer",
                display: "flex", alignItems: "center", gap: "5px",
                whiteSpace: "nowrap", transition: "background 0.2s",
              }}
            >
              {view === "form" ? <>📋 History</> : <>← Back</>}
            </button>
          </div>

          {/* Divider */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "22px" }}>
            <div style={{ flex: 1, height: "1px", background: "rgba(255,215,0,0.12)" }} />
            <span style={{
              width: "7px", height: "7px", borderRadius: "50%",
              background: C.accent, boxShadow: `0 0 5px ${C.accent}`,
            }} />
            <div style={{ flex: 1, height: "1px", background: "rgba(255,215,0,0.12)" }} />
          </div>

          {/* ── Body: form OR history ── */}
          {view === "history" ? (
            <HistoryView />
          ) : (
            <>
              {/* Wallet balance display */}
              <label style={{ color: C.textMuted, fontSize: "12px", fontWeight: "600", display: "block", marginBottom: "8px" }}>
                Current Balance
              </label>
              <div style={{
                padding: "13px 16px", background: C.inputBg, borderRadius: "12px",
                border: C.border, marginBottom: "16px",
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <span style={{ color: C.gold, fontWeight: "700", fontSize: "18px" }}>
                  ${currentBalance.toFixed(2)}
                </span>
                <span style={{ color: C.textDim, fontSize: "11px", letterSpacing: "0.5px" }}>USDT</span>
              </div>

              {/* Wallet type toggle */}
              <label style={{ color: C.textMuted, fontSize: "12px", fontWeight: "600", display: "block", marginBottom: "8px" }}>
                Source Wallet
              </label>
              <div style={{
                display: "flex", borderRadius: "10px", overflow: "hidden",
                border: C.border, background: C.inputBg, marginBottom: "20px",
              }}>
                {[
                  { value: "fund_wallet",  label: "Spot Wallet" },
                  { value: "topup_wallet", label: "Trade Wallet" },
                ].map(({ value, label }) => {
                  const active = walletType === value;
                  return (
                    <button
                      key={value}
                      onClick={() => setWalletType(value)}
                      style={{
                        flex: 1, padding: "11px 0", border: "none",
                        background: active ? "linear-gradient(135deg, #f5a623, #e6b422)" : "transparent",
                        color: active ? "#0d1117" : C.gold,
                        fontSize: "13px", fontWeight: "700", cursor: "pointer",
                        borderRight: value === "fund_wallet" ? "1px solid rgba(255,215,0,0.18)" : "none",
                        transition: "all 0.2s",
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>

              {/* Divider */}
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "18px" }}>
                <div style={{ flex: 1, height: "1px", background: "rgba(255,215,0,0.10)" }} />
                <span style={{ color: C.textDim, fontSize: "10px", letterSpacing: "2px" }}>ENTER DETAILS</span>
                <div style={{ flex: 1, height: "1px", background: "rgba(255,215,0,0.10)" }} />
              </div>

              {/* Transfer Amount */}
              <label style={{ color: C.textMuted, fontSize: "12px", fontWeight: "600", display: "block", marginBottom: "7px" }}>
                Transfer Amount (USDT)
              </label>
              <input
                type="number"
                placeholder="0.00"
                value={transferAmount}
                onChange={(e) => setTransferAmount(e.target.value)}
                min="0"
                style={{
                  width: "100%", padding: "15px 16px", background: C.inputBg,
                  border: C.border, borderRadius: "12px", color: C.textPrimary,
                  fontSize: "18px", fontWeight: "600", outline: "none",
                  boxSizing: "border-box", marginBottom: "16px",
                  fontFamily: "inherit", transition: "border-color 0.2s",
                }}
                onFocus={(e) => { e.target.style.border = C.borderFocus; }}
                onBlur={(e)  => { e.target.style.border = C.border; }}
              />

              {/* Customer ID */}
              <label style={{ color: C.textMuted, fontSize: "12px", fontWeight: "600", display: "block", marginBottom: "7px" }}>
                {isSelfTransfer ? "Your Customer ID (Self Transfer)" : "Recipient Customer ID"}
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type="text"
                  placeholder="Enter Customer ID"
                  value={customerId}
                  onChange={(e) => { if (!isSelfTransfer) setCustomerId(e.target.value); }}
                  readOnly={isSelfTransfer}
                  style={{
                    width: "100%", padding: "15px 16px",
                    paddingRight: isSelfTransfer ? "44px" : "16px",
                    background: isSelfTransfer ? "rgba(13,17,23,0.7)" : C.inputBg,
                    border: isSelfTransfer
                      ? "1px solid rgba(0,229,255,0.2)"
                      : C.border,
                    borderRadius: "12px",
                    color: isSelfTransfer ? C.accent : C.textPrimary,
                    fontSize: "15px", fontWeight: "600", outline: "none",
                    boxSizing: "border-box",
                    marginBottom: recipientStatus !== "idle" ? "6px" : "20px",
                    fontFamily: "inherit", transition: "border-color 0.2s",
                    cursor: isSelfTransfer ? "not-allowed" : "text",
                    opacity: isSelfTransfer ? 0.85 : 1,
                  }}
                  onFocus={(e) => { if (!isSelfTransfer) e.target.style.border = C.borderFocus; }}
                  onBlur={(e)  => { if (!isSelfTransfer) e.target.style.border = C.border; }}
                />
                {isSelfTransfer && (
                  <span style={{
                    position: "absolute", right: "14px", top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: "15px", pointerEvents: "none",
                  }}>🔒</span>
                )}
              </div>

              {/* Recipient name feedback */}
              {recipientStatus !== "idle" && (
                <div style={{
                  marginBottom: "16px", paddingLeft: "4px",
                  display: "flex", alignItems: "center", gap: "6px",
                }}>
                  {recipientStatus === "loading" && (
                    <>
                      <span style={{
                        width: "10px", height: "10px", borderRadius: "50%",
                        border: "2px solid rgba(245,166,35,0.3)", borderTopColor: C.gold,
                        display: "inline-block", animation: "spin 0.7s linear infinite",
                      }} />
                      <span style={{ color: C.textDim, fontSize: "11px" }}>Looking up user...</span>
                    </>
                  )}
                  {recipientStatus === "found" && (
                    <>
                      <span style={{ color: C.green, fontSize: "13px" }}>✓</span>
                      <span style={{ color: C.green, fontSize: "12px", fontWeight: "600" }}>{recipientName}</span>
                    </>
                  )}
                  {recipientStatus === "invalid" && (
                    <>
                      <span style={{ color: C.red, fontSize: "13px" }}>✕</span>
                      <span style={{ color: C.red, fontSize: "12px" }}>{recipientName}</span>
                    </>
                  )}
                </div>
              )}

              {/* Submit button */}
              <button
                onClick={() => { if (validate()) setShowConfirm(true); }}
                disabled={submitting}
                style={{
                  width: "100%", padding: "17px",
                  background: submitting ? "rgba(0,181,120,0.35)" : C.greenBtn,
                  border: "none", borderRadius: "12px", color: "#fff",
                  fontSize: "16px", fontWeight: "700",
                  cursor: submitting ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                  boxShadow: submitting ? "none" : "0 4px 18px rgba(0,181,120,0.3)",
                  letterSpacing: "0.3px",
                }}
              >
                {submitting ? (
                  <>
                    <span style={{
                      width: "16px", height: "16px", borderRadius: "50%",
                      border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff",
                      display: "inline-block", animation: "spin 0.7s linear infinite",
                    }} />
                    Processing...
                  </>
                ) : (
                  <>⇄ Transfer Funds</>
                )}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Confirm modal ── */}
      {showConfirm && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
          backdropFilter: "blur(8px)", display: "flex", alignItems: "center",
          justifyContent: "center", zIndex: 9999, padding: "20px",
        }}>
          <div style={{
            background: C.cardBg, borderRadius: "20px",
            border: "1px solid rgba(245,166,35,0.3)",
            boxShadow: "0 0 40px rgba(245,166,35,0.1)",
            padding: "32px 24px", textAlign: "center",
            maxWidth: "320px", width: "100%",
          }}>
            <div style={{
              width: "60px", height: "60px", borderRadius: "50%",
              background: "rgba(245,166,35,0.1)", border: `2px solid ${C.gold}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 18px", fontSize: "24px",
              boxShadow: "0 0 16px rgba(245,166,35,0.2)",
            }}>⇄</div>
            <h3 style={{ color: C.textPrimary, fontSize: "18px", fontWeight: "700", marginBottom: "10px" }}>
              Confirm Transfer
            </h3>
            <div style={{
              background: C.inputBg, borderRadius: "12px", border: C.border,
              padding: "14px", marginBottom: "20px", textAlign: "left",
            }}>
              {[
                { label: "Amount",      value: `$${Number(transferAmount).toFixed(2)} USDT`, color: C.gold },
                { label: "Wallet",      value: walletType === "fund_wallet" ? "Spot Wallet" : "Trade Wallet" },
                { label: "To",          value: recipientName || customerId, color: C.green },
                { label: "Customer ID", value: customerId },
              ].map(({ label, value, color }) => (
                <div key={label} style={{
                  display: "flex", justifyContent: "space-between",
                  marginBottom: "8px", alignItems: "center",
                }}>
                  <span style={{ color: C.textDim, fontSize: "12px" }}>{label}</span>
                  <span style={{ color: color || C.textPrimary, fontSize: "13px", fontWeight: "600" }}>{value}</span>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={() => setShowConfirm(false)}
                style={{
                  flex: 1, padding: "13px",
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: "12px", color: C.textMuted,
                  fontSize: "14px", fontWeight: "600", cursor: "pointer",
                }}
              >Cancel</button>
              <button
                onClick={handleSubmit}
                style={{
                  flex: 1, padding: "13px", background: C.greenBtn,
                  border: "none", borderRadius: "12px", color: "#fff",
                  fontSize: "14px", fontWeight: "700", cursor: "pointer",
                  boxShadow: "0 4px 16px rgba(0,181,120,0.3)",
                }}
              >Confirm</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin  { to { transform: rotate(360deg); } }
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 6px #00e5ff; opacity: 1; }
          50%       { box-shadow: 0 0 14px #00e5ff; opacity: 0.7; }
        }
      `}</style>
    </div>
  );
}
